import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Check, AlertCircle } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/stores/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BrandMark } from '@/components/brand/BrandMark';

export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signatureAgreed, setSignatureAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordValid = password.length >= 12;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = passwordValid && passwordsMatch && signatureAgreed && !!token;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post('/api/auth/accept-invitation', {
        token,
        password,
        signatureAgreed,
      });
      setSession(res.data);
      toast.success('Account activated', `Welcome, ${res.data.user.firstName}!`);
      navigate('/', { replace: true });
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return <InvalidTokenView />;
  }

  return (
    <AuthShell>
      <form onSubmit={onSubmit} className="animate-fade-in space-y-5">
        <div>
          <h2 className="font-display text-2xl">Accept invitation</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Finish setting up your Praxara account.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pw">Choose a password</Label>
          <Input
            id="pw"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordStrength password={password} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="cpw">Confirm password</Label>
          <Input
            id="cpw"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {confirmPassword.length > 0 && !passwordsMatch && (
            <p className="text-xs text-destructive">Passwords don&apos;t match</p>
          )}
        </div>

        <div className="rounded-lg border bg-muted/40 p-4 text-sm">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-primary"
              checked={signatureAgreed}
              onChange={(e) => setSignatureAgreed(e.target.checked)}
            />
            <div className="space-y-1">
              <div className="font-medium">21 CFR Part 11 Electronic Signature Agreement</div>
              <p className="text-xs text-muted-foreground">
                I agree that my electronic signature (password + reason) executed through Praxara is the legal equivalent of my handwritten signature and is binding under 21 CFR Section 11.100-Section 11.300.
              </p>
            </div>
          </label>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={!canSubmit || submitting}>
          {submitting ? 'Activating...' : 'Activate account'}
        </Button>
      </form>
    </AuthShell>
  );
}

function PasswordStrength({ password }: { password: string }) {
  const rules = [
    { ok: password.length >= 12, label: 'At least 12 characters' },
    { ok: /[A-Z]/.test(password), label: 'One uppercase letter (recommended)' },
    { ok: /[0-9]/.test(password), label: 'One digit (recommended)' },
    { ok: /[^a-zA-Z0-9]/.test(password), label: 'One symbol (recommended)' },
  ];
  return (
    <ul className="mt-1 space-y-0.5 text-xs">
      {rules.map((r) => (
        <li key={r.label} className="flex items-center gap-1.5">
          {r.ok ? (
            <Check className="h-3 w-3 text-emerald-600" />
          ) : (
            <span className="h-3 w-3 rounded-full border border-border" />
          )}
          <span className={r.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}>
            {r.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

function InvalidTokenView() {
  return (
    <AuthShell>
      <div className="space-y-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
          <AlertCircle className="h-5 w-5" />
        </div>
        <h2 className="font-display text-2xl">Invalid invitation</h2>
        <p className="text-sm text-muted-foreground">
          This invitation link is missing a token or has expired. Contact your administrator for a new invite.
        </p>
        <Button asChild variant="outline">
          <a href="/login">Go to sign-in</a>
        </Button>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5">
      <aside className="relative hidden overflow-hidden bg-primary p-10 text-primary-foreground lg:col-span-2 lg:flex lg:flex-col lg:justify-between">
        <BrandMark variant="light" />
        <div>
          <h1 className="font-display text-4xl leading-tight">
            Pharma-grade AI, designed for regulators.
          </h1>
          <div className="mt-8 grid gap-2 text-sm">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> EU AI Act · high-risk ready</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> GDPR · data processor agreement</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> ISO/IEC 27001 · SoA documented</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> 21 CFR Part 11 · audit + e-signatures</span>
          </div>
        </div>
        <div className="text-xs text-primary-foreground/60">(c) {new Date().getFullYear()} Praxara Ltd</div>
      </aside>
      <div className="flex items-center justify-center p-8 lg:col-span-3">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden"><BrandMark variant="dark" /></div>
          {children}
        </div>
      </div>
    </div>
  );
}
